/**
 * Maps `/getInteractedCustomerDetail/*` API routes to static files under
 * `public/getInteractedCustomerDetail/` using `staticInteractedManifest.json`.
 */

import staticManifest from "./staticInteractedManifest.json";

export const INTERACTED_PREFIX = "/getInteractedCustomerDetail";

/** Legacy/live paths that map to mocks under getInteractedCustomerDetail/. */
const STATIC_PATH_ALIASES = [
  [
    /^\/googledialogflow\/getdetailsdialogflow(\/|$)/,
    "/getInteractedCustomerDetail/getdetailsdialogflow$1",
  ],
];

/** Sample ids from HAR captures (used to pick fallback mocks per endpoint). */
export const SAMPLE_PHONE_ID = "1027703567103854";
export const SAMPLE_PROJECT_ID = "69f5b3aa2664fa3d3de69df3";

/** @deprecated use SAMPLE_PHONE_ID */
export const INACTIVE_CUSTOMER_SAMPLE_PHONE_ID = SAMPLE_PHONE_ID;

/** @type {{ exact: Set<string>; endpointSamples: Map<string, string> } | null} */
let manifestIndex = null;

function getManifestIndex() {
  if (manifestIndex) return manifestIndex;

  const urls = staticManifest.urls || [];
  const exact = new Set(urls);
  /** @type {Map<string, string>} base endpoint -> sample .json url */
  const endpointSamples = new Map();

  for (const url of urls) {
    if (!url.endsWith(".json")) continue;
    const pathname = url.split("?")[0];
    if (!pathname.startsWith(INTERACTED_PREFIX)) continue;

    const rest = pathname.slice(INTERACTED_PREFIX.length).replace(/^\//, "");
    const segments = rest.split("/").filter(Boolean);
    if (segments.length < 2) continue;

    const base = `${INTERACTED_PREFIX}/${segments[0]}`;
    const current = endpointSamples.get(base);
    const preferred =
      url.includes(SAMPLE_PHONE_ID) || url.includes(SAMPLE_PROJECT_ID);

    if (!current || preferred) {
      endpointSamples.set(base, url);
    }
  }

  manifestIndex = { exact, endpointSamples };
  return manifestIndex;
}

function onlyManifestUrls(urls) {
  const { exact } = getManifestIndex();
  return [...new Set(urls.filter((u) => exact.has(u)))];
}

/**
 * @param {string} base e.g. /getInteractedCustomerDetail/fetchagent
 */
function getManifestSampleForEndpoint(base) {
  return getManifestIndex().endpointSamples.get(base) || null;
}

export function normalizeToManifestPath(path) {
  const pathname = path.split("?")[0];
  let normalized = pathname;
  for (const [from, to] of STATIC_PATH_ALIASES) {
    normalized = normalized.replace(from, to);
  }
  return normalized;
}

export function isInteractedCustomerPath(path) {
  return normalizeToManifestPath(path).startsWith(INTERACTED_PREFIX);
}

/**
 * @param {string} path API path (may include query)
 * @param {string} method HTTP method
 * @param {unknown} [payload] POST/PUT body
 * @returns {string[]} manifest URLs to try (same origin, served from public/)
 */
export function buildStaticCandidateUrls(path, method, payload) {
  const pathname = normalizeToManifestPath(path);
  if (!pathname.startsWith(INTERACTED_PREFIX)) return [];
  const { exact } = getManifestIndex();
  const urls = [];

  if (
    pathname === `${INTERACTED_PREFIX}/gettotalremainingquota` ||
    pathname.endsWith("/gettotalremainingquota")
  ) {
    const phonenoId =
      payload && typeof payload === "object" && "phonenoId" in payload
        ? String(payload.phonenoId ?? "").trim()
        : "";
    const empty = `${INTERACTED_PREFIX}/gettotalremainingquota.json`;
    const ok = `${INTERACTED_PREFIX}/gettotalremainingquota_1.json`;

    if (!phonenoId) {
      return onlyManifestUrls([empty]);
    }
    if (exact.has(ok)) urls.push(ok);
    if (exact.has(empty)) urls.push(empty);
    return onlyManifestUrls(urls);
  }

  if (!pathname.endsWith(".json") && exact.has(`${pathname}.json`)) {
    urls.push(`${pathname}.json`);
  }
  if (exact.has(pathname)) {
    urls.push(pathname);
  }

  const rest = pathname.slice(INTERACTED_PREFIX.length).replace(/^\//, "");
  const segments = rest.split("/").filter(Boolean);
  if (segments.length >= 2) {
    const base = `${INTERACTED_PREFIX}/${segments[0]}`;
    const sample = getManifestSampleForEndpoint(base);
    const requestedId = segments.slice(1).join("/");
    if (sample && !sample.includes(requestedId)) {
      urls.push(sample);
    }
  }

  if (method !== "GET" && method !== "HEAD") {
    const alt = `${pathname}_1.json`;
    if (exact.has(alt)) urls.push(alt);
  }

  return onlyManifestUrls(urls);
}

/**
 * @param {string} url
 */
async function loadStaticJson(url) {
  const pathname = url.split("?")[0];
  if (!getManifestIndex().exact.has(pathname)) {
    return null;
  }

  try {
    const res = await fetch(url, { method: "GET", credentials: "include" });
    if (!res.ok) return null;
    const text = await res.text();
    if (!text.trim()) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function shouldUseStaticInteracted() {
  if (process.env.REACT_APP_USE_STATIC_JSON === "false") return false;
  if (
    process.env.REACT_APP_USE_STATIC_JSON === "true" ||
    process.env.REACT_APP_STATIC_ONLY === "true"
  ) {
    return true;
  }
  return false;
}

/**
 * @param {string} method
 * @param {string} path
 * @param {unknown} [payload]
 * @returns {Promise<object|string|null>}
 */
export async function fetchStaticInteractedResponse(method, path, payload) {
  if (!shouldUseStaticInteracted() || !isInteractedCustomerPath(path)) {
    return null;
  }

  const candidates = buildStaticCandidateUrls(path, method, payload);
  for (const url of candidates) {
    const body = await loadStaticJson(url);
    if (body !== null) return body;
  }
  return null;
}

/** All routes listed in the manifest (for tooling / docs). */
export function getManifestUrls() {
  return staticManifest.urls || [];
}
